import { AfterViewInit, Component, ElementRef, Inject, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { OwnerConfig } from '../../../../shared/interfaces/core/owner-config';
import { Commons } from '../../../../shared/Commons';
import { DomSanitizer } from '@angular/platform-browser';
import { StorageService } from '../../../../services/storage.service';
import { ScrollService } from '../../../../services/scroll.service';
import { ClientBrowserSessionStorageService } from '../../../../services/client-browser-session-storage.service';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@ngneat/transloco';
import { FullscreenAlertComponent } from '../../../../shared/modals/fullscreen-alert/fullscreen-alert.component';
import { Loader } from '@googlemaps/js-api-loader';
import { CustomCommons } from '../../../../shared/CustomCommons';

declare var google: any;

/*
Can BE:
  ...
  templateUrl: './custom/{CUSTOM_COMPONENT_NAME}.component.html'
  styleUrls: ['./custom/{CUSTOM_COMPONENT_NAME}.component.scss']
  ...
*/
@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    RouterModule,
    FullscreenAlertComponent
  ],
  templateUrl: './custom/footer.component.html',
  styleUrl: './custom/footer.component.scss'
})
export class FooterComponent implements OnInit, AfterViewInit {
  version = environment.appVersion

  ownerDetail: OwnerConfig = Commons.emptyOwnerConfig()

  privacyPolicyPath: string = Commons.PATH_TERMS + '/' + Commons.TERM_CODES[0].code
  termConditionsPath: string = Commons.PATH_TERMS + '/' + Commons.TERM_CODES[1].code
  termSalesPath: string = Commons.PATH_TERMS + '/' + Commons.TERM_CODES[2].code
  cookiePolicyPath: string = Commons.PATH_TERMS + '/' + Commons.TERM_CODES[3].code

  PATH_ABOUT = '/' + Commons.PATH_ABOUT
  PATH_SERVICES = '/' + Commons.PATH_SERVICES
  PATH_TEAM = '/' + Commons.PATH_TEAM
  PATH_CONTACT = '/' + Commons.PATH_CONTACT
  PATH_LOCATION = '/' + Commons.PATH_LOCATION
  YOUTUBE_LINK = environment.social.youtube
  INSTAGRAM_LINK = environment.social.instagram
  TWITTER_LINK = environment.social.x
  FACEBOOK_LINK = environment.social.facebook
  LINKEDIN_LINK = environment.social.linkedin
  MAP_FOOTER = 'footer'
  PUBLINEX_LINK = environment.productSite;

  anio: Date = new Date();
  ciaName: string = Commons.SDX
  companyName = this.ciaName
  appName = environment.appName
  providerWeb = environment.providerWeb

  mapEnabled = environment.home.map === this.MAP_FOOTER
  logoEnabled = environment.withFooterLogo
  @ViewChild('map', { static: false }) mapDiv!: ElementRef<HTMLDivElement>;
  map: any;
  logoUrl: any
  loadingLogo: boolean = false
  DF_LOGO = environment.ownerId != 0 ? Commons.DF_COMPANY_LOGO : Commons.DF_PRODUCT_LOGO
  showAlert: boolean = false
  mobileWidth: number = Commons.MOBILE_WIDTH
  getScreenWidth = this.mobileWidth + 1
  SERVICES_PAGE = 'page'
  SERVICES_HOME = 'home'
  showServiceType = environment.home.services
  TEAM_PAGE = 'page'
  TEAM_HOME = 'home'
  showTeamType = environment.home.team
  ABOUTUS_PAGE = 'page'
  ABOUTUS_HOME = 'home'
  showAboutUsType = environment.home.about_us
  CONTACT_PAGE = 'page'
  CONTACT_HOME = 'home'
  showContactType = environment.home.contact
  MAP_PAGE = 'page'
  MAP_HOME = 'home'
  showMapType = environment.home.map
  /* BEGIN | CUSTOM VARS */
  PATH_DESIGN = '/web-sites-design'
  PATH_FEATURES = '/web-sites-build'
  /* END | CUSTOM VARS */

  constructor(
    private sanitizer: DomSanitizer,
    private storageService: StorageService,
    private router: Router,
    private scrollService: ScrollService,
    private sessionStorageService: ClientBrowserSessionStorageService,
    @Inject(PLATFORM_ID) private platformId: Object
    /* BEGIN | CUSTOM ARGS */
    /* END | CUSTOM ARGS */
  ) {

  }

  ngAfterViewInit(): void {
    if (Commons.getOwner(this.sessionStorageService)) {
      this.reviewPlan(Commons.getOwner(this.sessionStorageService).plan_category)
    }
    this.initMap();
  }

  ngOnInit(): void {
    /* BEGIN | CUSTOM CODE */
    /* END | CUSTOM CODE */
    this.getScreenWidth = this.isBrowser ? window.innerWidth : Commons.MOBILE_WIDTH + 1
    this.ownerDetail = (Commons.getOwner(this.sessionStorageService) != null) ? Commons.getOwner(this.sessionStorageService).config : Commons.getDefaultConfig()
    this.companyName = this.ownerDetail.company_name
    this.loadLogo()
  }

  get isBrowser() {
    return Commons.isBrowser(this.platformId);
  }

  reviewPlan(planCategory: string | undefined) {
    if (Commons.validField(planCategory)) {
      const namesArray = Commons.PLAN_CATEGORIES.map(obj => obj.value);
      const index = namesArray.indexOf(planCategory);
      if (index === -1) {
        this.showAlert = true
      } else {
        this.showAlert = false
      }
    }
  }

  async initMap() {
    if (this.isBrowser && this.mapEnabled) {
      const loader = new Loader({
        apiKey: environment.map.gmapsApiKey,
        version: 'weekly',
        libraries: ['places', 'marker']
      });

      await loader.load();

      const { lat, lng } = environment.map;
      const map = new google.maps.Map(this.mapDiv.nativeElement, {
        center: { lat, lng },
        zoom: 15,
        mapId: environment.map.gmapID
      });

      new google.maps.marker.AdvancedMarkerElement({ position: { lat, lng }, map });
    }
  }

  get location(): string {
    const address = this.ownerDetail.address
    const city = this.ownerDetail.city
    const country = this.ownerDetail.country
    return (Commons.validField(address) && address !== '') ? address + ', ' + city + ', ' + country.toUpperCase() : city + ', ' + country.toUpperCase()
  }

  loadLogo() {
    if (Commons.validField(Commons.getOwner(this.sessionStorageService))) {
      this.loadingLogo = true
      this.storageService.getCompanyLogoByOwnerId(Commons.getOwner(this.sessionStorageService).id).subscribe(
        {
          next: async (v) => {
            this.logoUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(v))
            this.loadingLogo = false
          },
          error: (e) => {
            this.logoUrl = this.sanitizer.bypassSecurityTrustUrl(this.DF_LOGO)
            this.loadingLogo = false
          },
          complete: () => { }
        }
      )
    } else {
      this.logoUrl = this.sanitizer.bypassSecurityTrustUrl(this.DF_LOGO)
    }
  }

  get loadingGif() {
    return Commons.getLoadingGif(false)
  }

  get showAlertComponent() {
    const isLoginPage = this.router.isActive(Commons.PATH_LOGIN, true);
    return !isLoginPage && this.showAlert
  }

  get enableSocialLink(): boolean {
    const socialLinks = [this.YOUTUBE_LINK, this.INSTAGRAM_LINK, this.TWITTER_LINK, this.FACEBOOK_LINK, this.LINKEDIN_LINK];
    return socialLinks.some(link => link !== '');
  }

  get isMobile() {
    return this.getScreenWidth < this.mobileWidth
  }

  get isCurrentHomePath() {
    const currentRoute = this.router.url
    return currentRoute === `/${Commons.PATH_MAIN}`
  }

  private goToSection(section: string) {
    if (!this.isCurrentHomePath) {
      this.router.navigate([`/${Commons.PATH_MAIN}`]).then(() => {
        this.scrollService.scrollTo(section);
      });
    } else {
      this.scrollService.scrollTo(section);
    }
  }

  onClickServices() {
    this.goToSection(Commons.SCROLLABLE_SERVICES)
  }

  onClickTeam() {
    this.goToSection(Commons.SCROLLABLE_TEAM)
  }

  onClickAboutUs() {
    this.goToSection(Commons.SCROLLABLE_ABOUTUS)
  }

  onClickContact() {
    this.goToSection(Commons.SCROLLABLE_CONTACT)
  }

  onClickMap() {
    this.goToSection(Commons.SCROLLABLE_MAP)
  }

  /* BEGIN | CUSTOM FUNCTIONS */
  onClickPlans() {
    this.goToSection(CustomCommons.SCROLLABLE_PLANS)
  }
  /* END | CUSTOM FUNCTIONS */
}
